/**
 * TestCatalogPage.tsx — Test Catalogue admin page (master-detail rebuild).
 * PR B (qa/test-catalog-modernize-2026-07-11/PLAN.md).
 *
 * The "one object" UX (D1): pick a test on the left, edit EVERYTHING about it
 * on the right — commercial details, price, machine time, required skill,
 * machines, lab mapping, prerequisites, methodology — with ONE Save (the
 * transactional PUT /api/admin/test-definitions/:key). Creating a test also
 * creates its schedulable task type (D2, default ON), so tests created here
 * are schedulable from day one.
 *
 * Layout mirrors the user-approved Test Mapping master-detail (D5):
 *   TestDefinitionListColumn — searchable/filterable master list (B1)
 *   TestDefinitionDetailPane — the one-object form (B2/B3)
 *   OperationalStepsDialog   — the recipe-less prep task types (B4)
 *   SkillsDrawer             — skills management side panel (B5)
 * Prerequisites/Methodology editors are reused unchanged (D6). Import CSV is
 * kept as-is. The Task Types dialog + old detail drawer are retired (B6).
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Paper, Snackbar, Typography,
  alpha, useTheme,
} from '@mui/material';
import {
  AccountTree, Add, Article, FileUpload, ListAlt, PrecisionManufacturing,
  PsychologyAlt,
} from '@mui/icons-material';
import { ConfirmDialog, CONFIRM_INITIAL, type ConfirmDialogState } from '../../../components/shared/ConfirmDialog';
import { PageHelpButton } from '../../../components/help/PageHelpButton';
import { importTestCatalog } from '../../../services/testCatalogService';
import {
  addRecipeTaskType,
  createTestDefinition,
  deleteTestDefinition,
  fetchDeleteImpact,
  updateTestDefinition,
  type CreateDefinitionRequest,
  type TestDefinitionItem,
  type UpdateDefinitionRequest,
} from '../../../services/testDefinitionService';
import { createTaskType } from '../../../services/scheduleTaxonomy';
import type { CreateTestCatalogItemRequest } from '../../../types/testCatalogItem';
import { TestCatalogImportDialog } from './TestCatalogImportDialog';
import { PrerequisitesPanelDialog } from './PrerequisitesPanelDialog';
import { MethodologyPanelDialog } from './MethodologyPanelDialog';
import { MethodologyDialog } from './MethodologyDialog';
import { SkillsDrawer } from './SkillsDrawer';
import { OperationalStepsDialog } from './OperationalStepsDialog';
import { TaskTypeMachinesEditor } from './TaskTypeMachinesEditor';
import { TestDefinitionListColumn } from './TestDefinitionListColumn';
import { TestDefinitionDetailPane, type MachinesEditorTarget } from './TestDefinitionDetailPane';
import { useTestDefinitions } from './useTestDefinitions';
import {
  countByFilter,
  filterAndSortDefinitions,
  type DefinitionListFilter,
} from './testDefinitionModel';

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'info';
}

const apiErrorMessage = (err: unknown, fallback: string): string =>
  (err as { response?: { data?: { error?: string } } }).response?.data?.error
  ?? (err instanceof Error ? err.message : fallback);

export const TestCatalogPage = () => {
  const theme = useTheme();
  const data = useTestDefinitions();

  // List state (B1)
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [filter, setFilter] = useState<DefinitionListFilter>('all');
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [createMode, setCreateMode] = useState(false);

  // Detail-pane plumbing
  const [saving, setSaving] = useState(false);
  const [repairing, setRepairing] = useState(false);
  const [resetToken, setResetToken] = useState(0);
  const dirtyRef = useRef(false);
  const handleDirtyChange = useCallback((dirty: boolean) => { dirtyRef.current = dirty; }, []);

  // Dialogs / drawers
  const [confirm, setConfirm] = useState<ConfirmDialogState>(CONFIRM_INITIAL);
  const [importOpen, setImportOpen] = useState(false);
  const [prereqsOpen, setPrereqsOpen] = useState(false);
  const [prereqFocusItem, setPrereqFocusItem] = useState<TestDefinitionItem | null>(null);
  const [methodologyPanelOpen, setMethodologyPanelOpen] = useState(false);
  const [methodologyItem, setMethodologyItem] = useState<TestDefinitionItem | null>(null);
  const [skillsOpen, setSkillsOpen] = useState(false);
  const [operationalOpen, setOperationalOpen] = useState(false);
  const [machinesTarget, setMachinesTarget] = useState<MachinesEditorTarget | null>(null);

  const [snackbar, setSnackbar] = useState<SnackbarState>({ open: false, message: '', severity: 'info' });
  const notify = useCallback((message: string, severity: SnackbarState['severity'] = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  // ── Derived list ────────────────────────────────────────────────────────────

  const counts = useMemo(() => countByFilter(data.items), [data.items]);
  const listItems = useMemo(
    () => filterAndSortDefinitions(data.items, search, category, filter),
    [data.items, search, category, filter],
  );
  const selectedItem = useMemo(
    () => (selectedKey
      ? data.items.find((i) => i.TestCatalogItemKey === selectedKey) ?? null
      : null),
    [data.items, selectedKey],
  );
  const activeItems = useMemo(() => data.items.filter((i) => i.IsActive), [data.items]);

  // ── Dirty-state guard (B2): confirm before abandoning unsaved edits ────────

  const withDirtyGuard = useCallback((action: () => void) => {
    if (!dirtyRef.current) { action(); return; }
    setConfirm({
      open: true,
      title: 'Discard unsaved changes?',
      message: 'This test has unsaved edits. Leaving now discards them.',
      confirmLabel: 'Discard changes',
      confirmColor: 'warning',
      onConfirm: action,
    });
  }, []);

  const handleSelect = useCallback((key: string) => {
    if (key === selectedKey && !createMode) return;
    withDirtyGuard(() => {
      setCreateMode(false);
      setSelectedKey(key);
    });
  }, [selectedKey, createMode, withDirtyGuard]);

  const handleAddTest = useCallback(() => {
    withDirtyGuard(() => {
      setSelectedKey(null);
      setCreateMode(true);
    });
  }, [withDirtyGuard]);

  const handleCancelCreate = useCallback(() => setCreateMode(false), []);

  // ── Mutations (one Save → unified PUT; B2/B3) ───────────────────────────────

  const handleSave = useCallback(async (payload: UpdateDefinitionRequest) => {
    if (!selectedKey) return;
    setSaving(true);
    try {
      await updateTestDefinition(selectedKey, payload);
      await data.refresh();
      setResetToken((t) => t + 1);
      notify('Test saved');
    } catch (err) {
      notify(apiErrorMessage(err, 'Failed to save the test'), 'error');
    } finally {
      setSaving(false);
    }
  }, [selectedKey, data, notify]);

  const handleCreate = useCallback(async (payload: CreateDefinitionRequest) => {
    setSaving(true);
    try {
      const result = await createTestDefinition(payload);
      await data.refresh();
      setCreateMode(false);
      setSelectedKey(result.catalog.TestCatalogItemKey);
      notify(payload.createTaskType
        ? (result.linkedExistingTaskType
          ? 'Test created — linked to its existing task type'
          : 'Test created with its matching task type — schedulable from day one')
        : 'Test created (no task type — not schedulable until a recipe step is added)');
    } catch (err) {
      notify(apiErrorMessage(err, 'Failed to create the test'), 'error');
    } finally {
      setSaving(false);
    }
  }, [data, notify]);

  /** Delete: pre-check impact (A5) → ConfirmDialog → DELETE. */
  const handleDelete = useCallback(async (item: TestDefinitionItem) => {
    let message = `Delete "${item.TestName}" from the catalogue?`;
    try {
      const impact = await fetchDeleteImpact(item.TestCatalogItemKey);
      if (impact.wouldDeactivateTaskType && impact.primaryTaskType) {
        message += `\n\nIts scheduling task type "${impact.primaryTaskType.Name}" will be deactivated too.`;
        if (impact.liveTaskCount > 0) {
          message += `\n${impact.liveTaskCount} already-scheduled task${impact.liveTaskCount === 1 ? '' : 's'} keep${impact.liveTaskCount === 1 ? 's' : ''} referencing it (history stays intact); no NEW tasks will be generated.`;
        }
      } else if (impact.primaryTaskType && impact.sharedRecipeCount > 0) {
        message += `\n\nIts task type "${impact.primaryTaskType.Name}" stays active — ${impact.sharedRecipeCount} other test${impact.sharedRecipeCount === 1 ? "'s recipe uses" : "s' recipes use"} it.`;
      }
    } catch {
      message += '\n\n(Could not pre-check the scheduling impact — the matching task type is deactivated when it is safe to.)';
    }
    setConfirm({
      open: true,
      title: 'Delete test?',
      message,
      confirmLabel: 'Delete test',
      confirmColor: 'error',
      onConfirm: () => {
        void (async () => {
          setSaving(true);
          try {
            const result = await deleteTestDefinition(item.TestCatalogItemKey);
            await data.refresh();
            setSelectedKey(null);
            notify(result.taskTypeDeactivated
              ? 'Test deleted; its task type was deactivated'
              : 'Test deleted');
          } catch (err) {
            notify(apiErrorMessage(err, 'Failed to delete the test'), 'error');
          } finally {
            setSaving(false);
          }
        })();
      },
    });
  }, [data, notify]);

  /**
   * Defensive repair for an item with no primary task type: link a same-named
   * active task type if one exists, else create it (attended, no invented
   * minutes — D4), then add the recipe row.
   */
  const handleCreateMatchingTaskType = useCallback(async (item: TestDefinitionItem) => {
    setRepairing(true);
    try {
      const existing = data.allTaskTypes.find(
        (tt) => tt.Name.trim().toLowerCase() === item.TestName.trim().toLowerCase(),
      );
      const taskTypeKey = existing
        ? existing.TaskTypeKey
        : (await createTaskType({
          Name: item.TestName.trim(),
          Category: item.Category ?? undefined,
        })).TaskTypeKey;
      await addRecipeTaskType(item.TestCatalogItemKey, taskTypeKey);
      await Promise.all([data.refresh(), data.refreshLookups()]);
      notify(existing
        ? 'Linked the existing task type — this test is schedulable again'
        : 'Task type created and linked — set its machines and time next');
    } catch (err) {
      notify(apiErrorMessage(err, 'Failed to create the matching task type'), 'error');
    } finally {
      setRepairing(false);
    }
  }, [data, notify]);

  const handleImportComplete = useCallback(async (parsedItems: CreateTestCatalogItemRequest[]) => {
    try {
      const result = await importTestCatalog(parsedItems);
      setImportOpen(false);
      notify(`Imported ${result.created} item(s)`);
      await data.refresh();
    } catch {
      notify('Import failed', 'error');
    }
  }, [data, notify]);

  /** Machines & Time saved → capability counts + capable map both change. */
  const handleMachinesSaved = useCallback(async () => {
    setMachinesTarget(null);
    await Promise.all([data.refresh(), data.refreshLookups()]);
  }, [data]);

  // ── Render ──────────────────────────────────────────────────────────────────

  if (data.loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {/* Header: title + counts + toolbar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <ListAlt sx={{ color: theme.palette.primary.main }} />
        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.05rem' }}>
          Test Catalogue
        </Typography>
        <Chip
          size="small"
          label={`${counts.all} active test${counts.all === 1 ? '' : 's'}`}
          sx={{
            fontWeight: 700,
            backgroundColor: alpha(theme.palette.primary.main, 0.1),
            color: theme.palette.primary.main,
          }}
        />
        <Box sx={{ flex: 1 }} />
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button size="small" variant="outlined" startIcon={<AccountTree />}
            onClick={() => { setPrereqFocusItem(null); setPrereqsOpen(true); }}
            sx={{ textTransform: 'none' }}>
            Prerequisites
          </Button>
          <Button size="small" variant="outlined" startIcon={<Article />}
            onClick={() => setMethodologyPanelOpen(true)} sx={{ textTransform: 'none' }}>
            Methodology
          </Button>
          <Button size="small" variant="outlined" startIcon={<PsychologyAlt />}
            onClick={() => setSkillsOpen(true)} sx={{ textTransform: 'none' }}>
            Skills
          </Button>
          <Button size="small" variant="outlined" startIcon={<PrecisionManufacturing />}
            onClick={() => setOperationalOpen(true)} sx={{ textTransform: 'none' }}>
            Operational steps
          </Button>
          <Button size="small" variant="outlined" startIcon={<FileUpload />}
            onClick={() => setImportOpen(true)} sx={{ textTransform: 'none' }}>
            Import CSV
          </Button>
          <Button size="small" variant="contained" startIcon={<Add />}
            onClick={handleAddTest} sx={{ textTransform: 'none', fontWeight: 600 }}>
            Add Test
          </Button>
          <PageHelpButton pageKey="test-catalog" />
        </Box>
      </Box>

      {data.error && <Alert severity="error">{data.error}</Alert>}

      {/* Master-detail body */}
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', gap: 1.5 }}>
        <Paper
          variant="outlined"
          sx={{
            width: 400,
            flexShrink: 0,
            borderRadius: 2,
            borderColor: alpha(theme.palette.divider, 0.5),
            p: 1.5,
            minHeight: 0,
          }}
        >
          <TestDefinitionListColumn
            items={listItems}
            counts={counts}
            search={search}
            onSearchChange={setSearch}
            category={category}
            onCategoryChange={setCategory}
            filter={filter}
            onFilterChange={setFilter}
            selectedKey={selectedKey}
            onSelect={handleSelect}
            emptyMessage={data.items.length === 0
              ? 'No tests in the catalogue yet.'
              : 'No tests match the current filters.'}
          />
        </Paper>

        <Paper
          variant="outlined"
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            borderRadius: 2,
            borderColor: alpha(theme.palette.divider, 0.5),
            overflow: 'hidden',
          }}
        >
          <TestDefinitionDetailPane
            item={createMode ? null : selectedItem}
            createMode={createMode}
            totalActive={counts.all}
            skills={data.skills}
            machineOptions={data.machineOptions}
            capableMap={data.capableMap}
            allTaskTypes={data.allTaskTypes}
            saving={saving}
            repairing={repairing}
            resetToken={resetToken}
            onSave={(payload) => void handleSave(payload)}
            onCreate={(payload) => void handleCreate(payload)}
            onDelete={(item) => void handleDelete(item)}
            onDirtyChange={handleDirtyChange}
            onOpenMachinesEditor={setMachinesTarget}
            onCreateMatchingTaskType={(item) => void handleCreateMatchingTaskType(item)}
            onOpenPrerequisites={(item) => { setPrereqFocusItem(item); setPrereqsOpen(true); }}
            onOpenMethodology={setMethodologyItem}
            onRecipeChanged={() => void data.refresh()}
            onCancelCreate={handleCancelCreate}
          />
        </Paper>
      </Box>

      {/* Dialogs / drawers */}
      <TestCatalogImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImport={handleImportComplete}
      />
      <PrerequisitesPanelDialog
        open={prereqsOpen}
        onClose={() => { setPrereqsOpen(false); setPrereqFocusItem(null); void data.refresh(); }}
        allItems={activeItems}
        focusItemKey={prereqFocusItem?.TestCatalogItemKey ?? null}
      />
      <MethodologyPanelDialog
        open={methodologyPanelOpen}
        onClose={() => { setMethodologyPanelOpen(false); void data.refresh(); }}
        allItems={activeItems}
      />
      {methodologyItem && (
        <MethodologyDialog
          open={!!methodologyItem}
          onClose={() => { setMethodologyItem(null); void data.refresh(); }}
          testCatalogItemKey={methodologyItem.TestCatalogItemKey}
          testName={methodologyItem.TestName}
        />
      )}
      <SkillsDrawer
        open={skillsOpen}
        onClose={() => setSkillsOpen(false)}
        onChanged={() => void data.refreshLookups()}
      />
      <OperationalStepsDialog
        open={operationalOpen}
        onClose={() => setOperationalOpen(false)}
        steps={data.operationalTaskTypes}
        skills={data.skills}
        onChanged={() => { void data.refresh(); void data.refreshLookups(); }}
      />
      {machinesTarget && (
        <TaskTypeMachinesEditor
          open={!!machinesTarget}
          taskTypeKey={machinesTarget.key}
          taskTypeName={machinesTarget.name}
          runMode={machinesTarget.runMode}
          onClose={() => setMachinesTarget(null)}
          onSaved={() => void handleMachinesSaved()}
        />
      )}

      <ConfirmDialog state={confirm} onClose={() => setConfirm(CONFIRM_INITIAL)} />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
